import React, { useState } from "react";
import { Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import CardLessDataTableWithSearch from "../../datatables/CardLessDataTableWithSearch";
import { TopBottomChart, LeaderboardLineBarChart } from "./MarksChart";

const makeStudentState = (row) => {
    if (!row) return null;
    const studentId = row.student_id || row.id || row.user_id || row.student_user_id || row.edu_student_id;
    return {
        student_user_id: studentId,
        edu_student_id: row.edu_student_id || studentId,
        student_id: studentId,
        id: studentId,
        user_id: studentId,
        student_name: row.student_name || row.name || row.label,
        class_name: row.class_name || row.className,
        section_name: row.section_name || row.sectionName,
        admission_no: row.admission_no || row.roll_no,
        contact_no: row.contact_no || row.student_mobile || row.mobile || row.phone,
        student_mobile: row.student_mobile || row.contact_no || row.mobile || row.phone,
        is_academic_exist: true,
        ...row,
    };
};

const initials = (name) =>
    String(name || "?")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((p) => p[0])
        .join("")
        .toUpperCase();

function Avatar({ src, name, size = 32, bg = "#dbeafe", color = "#1d4ed8" }) {
    if (src) {
        return (
            <img
                src={src}
                alt={name}
                style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: "1px solid #e2e8f0" }}
            />
        );
    }
    return (
        <div
            style={{
                width: size,
                height: size,
                borderRadius: "50%",
                background: bg,
                color: color,
                fontWeight: 800,
                fontSize: size * 0.4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
            }}
        >
            {initials(name)}
        </div>
    );
}

const statusBadge = (status) => {
    const isPass = status === "Pass";
    return (
        <span
            style={{
                background: isPass ? "#dcfce7" : "#fee2e2",
                color: isPass ? "#15803d" : "#b91c1c",
                border: `1px solid ${isPass ? "#bbf7d0" : "#fecaca"}`,
                fontWeight: 700,
                fontSize: 11.5,
                padding: "3px 10px",
                borderRadius: 20,
                display: "inline-block",
            }}
        >
            {isPass ? "✓ Pass" : "✕ Fail"}
        </span>
    );
};

// keyField is hardcoded to "id" inside CardLessDataTableWithSearch — student rows
// already carry a unique `id` from the API, with an index fallback just in case.
const withRowId = (rows) => rows.map((r, idx) => ({ ...r, id: r.id ?? `row-${idx}`, rank: idx + 1 }));

// Optional "Report" action — appended only when the parent supplies an
// onPrintReportCard handler, so the callers that don't print stay untouched.
const reportCardColumn = (onPrintReportCard) => ({
    dataField: "reportCard",
    text: "Action",
    formatter: (cell, row) => (
        <button
            type="button"
            className="btn btn-sm"
            title="Print this student's Exam Report Card"
            style={{ background: "#eef2ff", color: "#4f46e5", border: "1px solid #c7d2fe", borderRadius: 8, fontSize: 11.5, fontWeight: 700, padding: "3px 9px", whiteSpace: "nowrap" }}
            onClick={() => onPrintReportCard(row)}
        >
            <i className="fal fa-print me-1" /> Report
        </button>
    ),
});

const withReportCardColumn = (columns, onPrintReportCard) =>
    (onPrintReportCard ? [...columns, reportCardColumn(onPrintReportCard)] : columns);

const leaderboardColumns = [
    {
        dataField: "rank",
        text: "Rank",
        sort: true,
        formatter: (cell) => {
            const r = Number(cell);
            if (r === 1) return <span style={{ background: "linear-gradient(135deg, #fef3c7, #fde68a)", color: "#92400e", border: "1px solid #fcd34d", padding: "3px 9px", borderRadius: 20, fontWeight: 800, fontSize: 11 }}>🥇 #1</span>;
            if (r === 2) return <span style={{ background: "linear-gradient(135deg, #f1f5f9, #e2e8f0)", color: "#334155", border: "1px solid #cbd5e1", padding: "3px 9px", borderRadius: 20, fontWeight: 800, fontSize: 11 }}>🥈 #2</span>;
            if (r === 3) return <span style={{ background: "linear-gradient(135deg, #ffedd5, #fed7aa)", color: "#9a3412", border: "1px solid #fdba74", padding: "3px 9px", borderRadius: 20, fontWeight: 800, fontSize: 11 }}>🥉 #3</span>;
            return <span style={{ background: "#f8fafc", color: "#475569", border: "1px solid #e2e8f0", padding: "2px 8px", borderRadius: 20, fontWeight: 700, fontSize: 11 }}>#{r}</span>;
        },
    },
    {
        dataField: "student_name",
        text: "Student",
        sort: true,
        formatter: (cell, row) => (
            <Link
                to={{ pathname: "/student-profile-1/overview", state: { student: makeStudentState(row) } }}
                className="d-flex align-items-center"
                style={{ gap: 8, textDecoration: "none" }}
            >
                <Avatar src={row.profile_pic} name={cell} size={28} />
                <span className="font-weight-bold" style={{ fontSize: 13, color: "#2563eb", textDecoration: "underline", textUnderlineOffset: 2 }}>{cell}</span>
            </Link>
        ),
    },
    {
        dataField: "class_name",
        text: "Class",
        sort: true,
        formatter: (cell) => (
            <span style={{ background: "#f5f3ff", color: "#7c3aed", padding: "2px 7px", borderRadius: 6, fontWeight: 600, fontSize: 11.5 }}>
                {cell || "-"}
            </span>
        ),
    },
    {
        dataField: "section_name",
        text: "Section",
        sort: true,
        formatter: (cell) => (
            <span style={{ background: "#ecfeff", color: "#0891b2", padding: "2px 7px", borderRadius: 6, fontWeight: 600, fontSize: 11.5 }}>
                {cell || "-"}
            </span>
        ),
    },
    {
        dataField: "marksNum",
        text: "Marks",
        sort: true,
        formatter: (cell) => (
            <span style={{ fontWeight: 800, color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "2px 8px", borderRadius: 8, fontSize: 12 }}>
                {cell ?? "-"}
            </span>
        ),
    },
    {
        dataField: "accuracyNum",
        text: "Accuracy",
        sort: true,
        formatter: (cell) => (
            <span style={{ fontWeight: 700, color: "#0369a1", background: "#f0f9ff", border: "1px solid #bae6fd", padding: "2px 8px", borderRadius: 8, fontSize: 12 }}>
                {cell}%
            </span>
        ),
    },
    { dataField: "exam_rank", text: "Exam Rank", sort: true, formatter: (cell) => <span style={{ fontWeight: 700, color: "#475569" }}>#{cell ?? "-"}</span> },
    // { dataField: "overall_rank", text: "Overall Rank", sort: true, formatter: (cell) => <span style={{ fontWeight: 700, color: "#475569" }}>#{cell ?? "-"}</span> },
    // { dataField: "passFail", text: "Status", sort: true, formatter: (cell) => statusBadge(cell) },
];

function StudentLeaderboard({ rows, defaultView = "table", showToggle = true, onPrintReportCard }) {
    const [viewMode, setViewMode] = useState(defaultView);
    const [graphType, setGraphType] = useState("composed"); // "composed" | "bar" | "line"

    if (!rows || rows.length === 0) {
        return <p className="text-muted text-center py-3 mb-0">No Students Found</p>;
    }

    const columns = withReportCardColumn(leaderboardColumns, onPrintReportCard);

    const renderGraphSelector = () => (
        <div style={{ background: "#f8fafc", border: "1px solid #cbd5e1", borderRadius: 6, padding: 2, display: "inline-flex", gap: 2 }}>
            <button
                type="button"
                className="btn btn-sm py-0 px-2"
                style={{ fontSize: 10.5, fontWeight: 700, border: "none", background: graphType === "composed" ? "#2563eb" : "transparent", color: graphType === "composed" ? "#fff" : "#64748b", borderRadius: 4 }}
                onClick={() => setGraphType("composed")}
            >
                Line & Bar
            </button>
            <button
                type="button"
                className="btn btn-sm py-0 px-2"
                style={{ fontSize: 10.5, fontWeight: 700, border: "none", background: graphType === "bar" ? "#2563eb" : "transparent", color: graphType === "bar" ? "#fff" : "#64748b", borderRadius: 4 }}
                onClick={() => setGraphType("bar")}
            >
                Bar
            </button>
            <button
                type="button"
                className="btn btn-sm py-0 px-2"
                style={{ fontSize: 10.5, fontWeight: 700, border: "none", background: graphType === "line" ? "#16a34a" : "transparent", color: graphType === "line" ? "#fff" : "#64748b", borderRadius: 4 }}
                onClick={() => setGraphType("line")}
            >
                Line
            </button>
        </div>
    );

    return (
        <div>
            {showToggle && (
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: 8 }}>
                    <div style={{ background: "#f1f5f9", borderRadius: 8, padding: 3, display: "inline-flex", gap: 3 }}>
                        <button
                            type="button"
                            className="btn btn-sm"
                            style={{
                                fontSize: 11.5,
                                fontWeight: 700,
                                padding: "4px 12px",
                                borderRadius: 6,
                                border: "none",
                                background: viewMode === "table" ? "#fff" : "transparent",
                                color: viewMode === "table" ? "#2563eb" : "#64748b",
                                boxShadow: viewMode === "table" ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                            }}
                            onClick={() => setViewMode("table")}
                        >
                            <i className="fal fa-table me-1" /> Table
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm"
                            style={{
                                fontSize: 11.5,
                                fontWeight: 700,
                                padding: "4px 12px",
                                borderRadius: 6,
                                border: "none",
                                background: viewMode === "graph" ? "#fff" : "transparent",
                                color: viewMode === "graph" ? "#2563eb" : "#64748b",
                                boxShadow: viewMode === "graph" ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                            }}
                            onClick={() => setViewMode("graph")}
                        >
                            <i className="fal fa-chart-bar me-1" /> Graph
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm"
                            style={{
                                fontSize: 11.5,
                                fontWeight: 700,
                                padding: "4px 12px",
                                borderRadius: 6,
                                border: "none",
                                background: viewMode === "both" ? "#fff" : "transparent",
                                color: viewMode === "both" ? "#2563eb" : "#64748b",
                                boxShadow: viewMode === "both" ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                            }}
                            onClick={() => setViewMode("both")}
                        >
                            <i className="fal fa-th-large me-1" /> Both
                        </button>
                    </div>
                </div>
            )}

            {viewMode === "both" ? (
                <Row className="g-3 align-items-stretch">
                    <Col lg={7} md={12}>
                        <CardLessDataTableWithSearch
                            parentData={withRowId(rows)}
                            particlecolumns={columns}
                            defaultSorted={[{ dataField: "rank", order: "asc" }]}
                            name="exam_leaderboard"
                            placeholder="Search leaderboard..."
                        />
                    </Col>
                    <Col lg={5} md={12}>
                        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16, height: "100%" }}>
                            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: 6 }}>
                                <h6 className="font-weight-bold text-dark mb-0" style={{ fontSize: 13 }}>
                                    <i className="fal fa-chart-line me-1 text-primary" /> Performance Graph
                                </h6>
                                {renderGraphSelector()}
                            </div>
                            <LeaderboardLineBarChart data={rows} height={320} chartType={graphType} />
                        </div>
                    </Col>
                </Row>
            ) : viewMode === "graph" ? (
                <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 }}>
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: 6 }}>
                        <h6 className="font-weight-bold text-dark mb-0" style={{ fontSize: 13 }}>
                            <i className="fal fa-chart-line me-1 text-primary" /> Performance Graph
                        </h6>
                        {renderGraphSelector()}
                    </div>
                    <LeaderboardLineBarChart data={rows} height={360} chartType={graphType} />
                </div>
            ) : (
                <CardLessDataTableWithSearch
                    parentData={withRowId(rows)}
                    particlecolumns={columns}
                    defaultSorted={[{ dataField: "rank", order: "asc" }]}
                    name="exam_leaderboard"
                    placeholder="Search leaderboard..."
                />
            )}
        </div>
    );
}

export function TopPerformerCards({ students }) {
    if (!students || students.length === 0) return null;
    return (
        <div className="d-flex flex-wrap mb-2" style={{ gap: 8 }}>
            {students.slice(0, 10).map((s, idx) => (
                <Link
                    key={s.id || idx}
                    to={{ pathname: "/student-profile-1/overview", state: { student: makeStudentState(s) } }}
                    className="text-center"
                    style={{
                        background: "#fff",
                        border: idx < 3 ? "1px solid #fcd34d" : "1px solid #e2e8f0",
                        borderRadius: 12,
                        padding: "10px 12px",
                        minWidth: 105,
                        flex: "1 1 100px",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.02)",
                        textDecoration: "none",
                    }}
                >
                    <div style={{ position: "relative", display: "inline-block" }}>
                        <Avatar src={s.profile_pic} name={s.student_name} size={40} bg="#f0fdf4" color="#16a34a" />
                        {idx < 3 && (
                            <span
                                style={{
                                    position: "absolute",
                                    top: -4,
                                    right: -4,
                                    background: idx === 0 ? "#f59e0b" : idx === 1 ? "#64748b" : "#c2410c",
                                    color: "#fff",
                                    borderRadius: "50%",
                                    width: 18,
                                    height: 18,
                                    fontSize: 9.5,
                                    fontWeight: 800,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    border: "1.5px solid #fff",
                                }}
                            >
                                {idx + 1}
                            </span>
                        )}
                    </div>
                    <div className="font-weight-bold text-dark mt-1" style={{ fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {s.student_name}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#16a34a", marginTop: 2 }}>{s.marksNum}</div>
                    <div className="text-muted" style={{ fontSize: 10.5 }}>Acc: {s.accuracyNum}%</div>
                </Link>
            ))}
        </div>
    );
}

const topPerformersColumns = [
    {
        dataField: "student_name",
        text: "Student",
        sort: true,
        formatter: (cell, row) => (
            <Link
                to={{ pathname: "/student-profile-1/overview", state: { student: makeStudentState(row) } }}
                className="d-flex align-items-center"
                style={{ gap: 8, textDecoration: "none" }}
            >
                <Avatar src={row.profile_pic} name={cell} size={28} bg="#f0fdf4" color="#16a34a" />
                <span className="font-weight-bold" style={{ fontSize: 13, color: "#2563eb", textDecoration: "underline", textUnderlineOffset: 2 }}>{cell}</span>
            </Link>
        ),
    },
    {
        dataField: "marksNum",
        text: "Marks",
        sort: true,
        formatter: (cell) => (
            <span style={{ fontWeight: 800, color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "2px 8px", borderRadius: 8, fontSize: 12 }}>
                {cell ?? "-"}
            </span>
        ),
    },
    {
        dataField: "accuracyNum",
        text: "Accuracy",
        sort: true,
        formatter: (cell) => (
            <span style={{ fontWeight: 700, color: "#0369a1", background: "#f0f9ff", border: "1px solid #bae6fd", padding: "2px 8px", borderRadius: 8, fontSize: 12 }}>
                {cell}%
            </span>
        ),
    },
    {
        dataField: "rank",
        text: "Rank",
        sort: true,
        formatter: (cell) => {
            const r = Number(cell);
            if (r === 1) return <span style={{ background: "linear-gradient(135deg, #fef3c7, #fde68a)", color: "#92400e", border: "1px solid #fcd34d", padding: "3px 9px", borderRadius: 20, fontWeight: 800, fontSize: 11 }}>🥇 #1</span>;
            if (r === 2) return <span style={{ background: "linear-gradient(135deg, #f1f5f9, #e2e8f0)", color: "#334155", border: "1px solid #cbd5e1", padding: "3px 9px", borderRadius: 20, fontWeight: 800, fontSize: 11 }}>🥈 #2</span>;
            if (r === 3) return <span style={{ background: "linear-gradient(135deg, #ffedd5, #fed7aa)", color: "#9a3412", border: "1px solid #fdba74", padding: "3px 9px", borderRadius: 20, fontWeight: 800, fontSize: 11 }}>🥉 #3</span>;
            return <span style={{ background: "#f8fafc", color: "#475569", border: "1px solid #e2e8f0", padding: "2px 8px", borderRadius: 20, fontWeight: 700, fontSize: 11 }}>#{r}</span>;
        },
    },
    // { dataField: "exam_rank", text: "/Rank", sort: true, formatter: (cell) => <span style={{ fontWeight: 700, color: "#16a34a" }}>#{cell ?? "-"}</span> },
];

export function TopPerformersTable({ students, onPrintReportCard }) {
    if (!students || students.length === 0) {
        return <p className="text-muted text-center py-3 mb-0">No Students Found</p>;
    }
    return (
        <CardLessDataTableWithSearch
            parentData={withRowId(students.slice(0, 10))}
            particlecolumns={withReportCardColumn(topPerformersColumns, onPrintReportCard)}
            defaultSorted={[{ dataField: "rank", order: "asc" }]}
            name="top_performers_table"
            placeholder="Search top students..."
        />
    );
}

const lowestPerformersColumns = [
    {
        dataField: "student_name",
        text: "Student",
        sort: true,
        formatter: (cell, row) => (
            <Link
                to={{ pathname: "/student-profile-1/overview", state: { student: makeStudentState(row) } }}
                className="d-flex align-items-center"
                style={{ gap: 8, textDecoration: "none" }}
            >
                <Avatar src={row.profile_pic} name={cell} size={28} bg="#fef2f2" color="#dc2626" />
                <span className="font-weight-bold" style={{ fontSize: 13, color: "#2563eb", textDecoration: "underline", textUnderlineOffset: 2 }}>{cell}</span>
            </Link>
        ),
    },
    {
        dataField: "marksNum",
        text: "Marks",
        sort: true,
        formatter: (cell) => (
            <span style={{ fontWeight: 800, color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", padding: "2px 8px", borderRadius: 8, fontSize: 12 }}>
                {cell ?? "-"}
            </span>
        ),
    },
    {
        dataField: "accuracyNum",
        text: "Accuracy",
        sort: true,
        formatter: (cell) => (
            <span style={{ fontWeight: 700, color: "#c2410c", background: "#fff7ed", border: "1px solid #ffedd5", padding: "2px 8px", borderRadius: 8, fontSize: 12 }}>
                {cell}%
            </span>
        ),
    },
    {
        dataField: "weakSubject",
        text: "Weak Subject",
        sort: true,
        formatter: (cell) => (
            <span style={{ background: "#f5f3ff", color: "#7c3aed", border: "1px solid #ddd6fe", padding: "2px 7px", borderRadius: 6, fontWeight: 600, fontSize: 11.5 }}>
                {cell || "General"}
            </span>
        ),
    },
    { dataField: "exam_rank", text: "Rank", sort: true, formatter: (cell) => <span style={{ fontWeight: 700, color: "#dc2626" }}>#{cell ?? "-"}</span> },
];

export function LowestPerformersTable({ students, onPrintReportCard }) {
    if (!students || students.length === 0) {
        return <p className="text-muted text-center py-3 mb-0">No Students Found</p>;
    }
    return (
        <CardLessDataTableWithSearch
            parentData={withRowId(students)}
            particlecolumns={withReportCardColumn(lowestPerformersColumns, onPrintReportCard)}
            defaultSorted={[{ dataField: "marksNum", order: "asc" }]}
            name="students_needing_support"
            placeholder="Search students..."
        />
    );
}

export default StudentLeaderboard;
